import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    SpreadsheetCellRef,
    SpreadsheetReference,
    SpreadsheetViewConfig,
} from "@common/schemas/spreadsheetReference.schema";
import {
    DraftRefsState,
    DraftRefsTarget,
    useDraftRefsByTarget,
    useTargetDraftRefs,
} from "./useDraftRefsByTarget";

/**
 * Draft cell references per composer, deduped by (source + taskId + cell
 * set), so each "Add to chat" adds the selection to what is already there.
 */
export interface SpreadsheetRefContextValue extends DraftRefsState<SpreadsheetReference> {
    /**
     * Imperative: scroll + highlight cells in the spreadsheet task body.
     * The task body registers itself by calling `registerFocusHandler`.
     */
    focusSpreadsheetReferences: (refs: SpreadsheetReference[]) => void;
    /** Called by the task body to register its highlight handler.
     *  Pass `null` to deregister. */
    registerFocusHandler: (
        handler: ((refs: SpreadsheetReference[]) => void) | null,
    ) => void;
    /**
     * View config currently active in the mounted PL/BS task body, or null
     * when no task body with spreadsheet support is mounted.
     */
    currentViewConfig: SpreadsheetViewConfig | null;
    /** Set by the active PL/BS task body on mount/update, cleared on unmount. */
    setCurrentViewConfig: (cfg: SpreadsheetViewConfig | null) => void;
    /**
     * Ask the active task body to restore its view to the snapshot stored in
     * `reference.viewConfig` and then highlight the referenced cells.
     */
    requestApplyViewConfig: (reference: SpreadsheetReference) => void;
    /**
     * Called by the task body to register its view-restore handler.
     * Pass `null` to deregister on unmount.
     */
    registerApplyViewConfigHandler: (
        handler: ((ref: SpreadsheetReference) => void) | null,
    ) => void;
}

/**
 * Narrow context for the current grid selection not yet committed to draft
 * refs. Split from SpreadsheetRefContext so that selection changes only
 * re-render subscribers of this context (e.g. BalanceSheetTaskCommandBar)
 * instead of every SpreadsheetRefContext consumer.
 */
interface PendingReferenceContextValue {
    pendingReference: SpreadsheetReference | null;
    setPendingReference: (ref: SpreadsheetReference | null) => void;
}

/**
 * Stable key used to dedupe draft refs when appending. Two refs targeting the
 * same task and the same set of cells collapse into one.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function buildDraftRefDedupKey(ref: SpreadsheetReference): string {
    const cellsKey = ref.cells
        .map((c: SpreadsheetCellRef) => `${c.rowKey}:${c.columnKey}`)
        .sort((a, b) => a.localeCompare(b))
        .join("|");
    return `${ref.source ?? ""}::${ref.taskId}::${cellsKey}`;
}

const SpreadsheetRefContext = createContext<SpreadsheetRefContextValue | null>(
    null,
);

const PendingReferenceContext =
    createContext<PendingReferenceContextValue | null>(null);

interface SpreadsheetRefProviderProps {
    /**
     * Current task id — draft refs are cleared automatically when this changes.
     * Null provides no context, so callers can keep the provider mounted (and
     * their subtree with it) across tasks that don't support cell references.
     */
    taskId: string | null;
    children: React.ReactNode;
}

export const SpreadsheetRefProvider: React.FC<SpreadsheetRefProviderProps> = ({
    taskId,
    children,
}) => {
    const {
        draftRefsByTarget,
        appendDraftRefs,
        clearDraftRefs,
        removeDraftRef,
    } = useDraftRefsByTarget({
        taskId: taskId ?? "",
        maxRefs: Number.POSITIVE_INFINITY,
        getDedupKey: buildDraftRefDedupKey,
    });
    const [pendingReference, setPendingReference] =
        useState<SpreadsheetReference | null>(null);
    const [currentViewConfig, setCurrentViewConfig] =
        useState<SpreadsheetViewConfig | null>(null);

    const focusHandlerRef = useRef<
        ((refs: SpreadsheetReference[]) => void) | null
    >(null);
    const applyViewConfigHandlerRef = useRef<
        ((ref: SpreadsheetReference) => void) | null
    >(null);

    // Reset the pending selection whenever the active task changes
    useEffect(() => {
        setPendingReference(null);
    }, [taskId]);

    const focusSpreadsheetReferences = useCallback(
        (refs: SpreadsheetReference[]) => {
            focusHandlerRef.current?.(refs);
        },
        [],
    );

    const registerFocusHandler = useCallback(
        (handler: ((refs: SpreadsheetReference[]) => void) | null) => {
            focusHandlerRef.current = handler;
        },
        [],
    );

    const registerApplyViewConfigHandler = useCallback(
        (handler: ((ref: SpreadsheetReference) => void) | null) => {
            applyViewConfigHandlerRef.current = handler;
        },
        [],
    );

    const requestApplyViewConfig = useCallback(
        (reference: SpreadsheetReference) => {
            applyViewConfigHandlerRef.current?.(reference);
        },
        [],
    );

    const refValue = useMemo(
        () => ({
            draftRefsByTarget,
            appendDraftRefs,
            clearDraftRefs,
            removeDraftRef,
            focusSpreadsheetReferences,
            registerFocusHandler,
            currentViewConfig,
            setCurrentViewConfig,
            requestApplyViewConfig,
            registerApplyViewConfigHandler,
        }),
        [
            draftRefsByTarget,
            appendDraftRefs,
            clearDraftRefs,
            removeDraftRef,
            focusSpreadsheetReferences,
            registerFocusHandler,
            currentViewConfig,
            setCurrentViewConfig,
            requestApplyViewConfig,
            registerApplyViewConfigHandler,
        ],
    );

    const pendingValue = useMemo(
        () => ({
            pendingReference,
            setPendingReference,
        }),
        [pendingReference, setPendingReference],
    );

    const isEnabled = taskId !== null;

    return (
        <SpreadsheetRefContext.Provider value={isEnabled ? refValue : null}>
            <PendingReferenceContext.Provider
                value={isEnabled ? pendingValue : null}
            >
                {children}
            </PendingReferenceContext.Provider>
        </SpreadsheetRefContext.Provider>
    );
};

/** Returns null instead of throwing — use outside provider boundaries */
// eslint-disable-next-line react-refresh/only-export-components
export function useSpreadsheetRefContextMaybe(): SpreadsheetRefContextValue | null {
    return useContext(SpreadsheetRefContext);
}

/**
 * Hook for reading/writing the pending grid-selection reference.
 * Uses a separate context from SpreadsheetRefContext so changes to the
 * selection don't cause re-renders in unrelated consumers.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function usePendingReferenceContext(): PendingReferenceContextValue {
    const ctx = useContext(PendingReferenceContext);
    if (!ctx) {
        throw new Error(
            "usePendingReferenceContext must be used inside SpreadsheetRefProvider",
        );
    }
    return ctx;
}

/** Returns null instead of throwing — use outside provider boundaries */
// eslint-disable-next-line react-refresh/only-export-components
export function usePendingReferenceContextMaybe(): PendingReferenceContextValue | null {
    return useContext(PendingReferenceContext);
}

/** Draft cell references and their remove handler for one composer. */
// eslint-disable-next-line react-refresh/only-export-components
export function useDraftRefsForTarget(target: DraftRefsTarget) {
    return useTargetDraftRefs({
        draftRefsState: useSpreadsheetRefContextMaybe(),
        target,
    });
}
